import { useInitialPublicData, usePublicBootstrap } from '../../seo/PublicBootstrap.jsx';
import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Input, PublicContainer } from '@mts/ui';
import { ChevronRight, Flame, Gift, Mail, Phone, Sparkles, Tag } from 'lucide-react';
import { usePublicTenant } from '../../hooks/usePublicTenant';
import { fetchHomepageListings } from '../../services/api';
import { getHomepageOffers } from '../../services/offersService';
import { getOrCreateVisitorId } from '../../services/visitorTracking';
import { resolveLightweightProfile } from '../../services/userProfiles';
import TrackableListingCard from '../common/TrackableListingCard';
import {
  buildDealPanelRows,
  buildEmptyPanelRow,
  buildNewBusinessPanelRows,
} from '../../utils/homeCommunityPanels';
import { getTierKey } from '../../utils/listingFeatures';
import { normalizePhoneToE164 } from '../../utils/phoneNumbers';
import PublicOptInPanel from '../public/PublicOptInPanel';
import { PUBLIC_SMS_CONSENT_TEXT, PUBLIC_SMS_CONSENT_VERSION } from '@mts/shared/smsConsent';

function PanelSkeletonRows() {
  return Array.from({ length: 5 }, (_, index) => (
    <div key={`panel-skeleton-${index}`} className="flex w-full items-center gap-3 px-4 py-3">
      <div className="h-14 w-14 shrink-0 animate-pulse rounded-md bg-slate-200" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-3 w-20 animate-pulse rounded-full bg-slate-200" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
      </div>
      <div className="h-3 w-16 animate-pulse rounded bg-slate-100" />
    </div>
  ));
}

function HomeCommunitySections() {
  const bootstrap = usePublicBootstrap();
  const [panelNow, setPanelNow] = useState(() => new Date(bootstrap?.renderedAt || Date.now()));
  useEffect(() => { setPanelNow(new Date()); }, []);
  const toast = useFeedback();
  const navigate = useNavigate();
  const { spotlightName, optInImageUrl, optInFocalX, optInFocalY, brandTheme } = usePublicTenant();

  const surfaceBorder = brandTheme?.border || '#E2E8F0';
  const headingColor = brandTheme?.text || '#0F172A';
  const subtleColor = brandTheme?.muted || '#6B7280';
  const inputBorder = brandTheme?.border || '#D1D5DB';
  const inputText = brandTheme?.text || '#0F172A';
  const heroGlowBlue = brandTheme?.primarySoft || '#DBEAFE';
  const heroGlowGreen = brandTheme?.secondarySoft || '#E2E8F0';
  const heroOverlay = brandTheme?.text || '#0F172A';
  const optInInputFocusColor = brandTheme?.primary || '#2563EB';
  const optInInputRingColor = brandTheme?.primary || '#2563EB';
  const optInRequest = useRef(null);
  const [optInForm, setOptInForm] = useState({ email: '', phone: '', smsMarketingConsent: false });
  const [optInSubmitting, setOptInSubmitting] = useState(false);
  const [optInError, setOptInError] = useState('');
  const initial = useInitialPublicData("homePanels");
  const [panelData, setPanelData] = useState(initial || {
    offers: [],
    listings: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (initial) { setPanelData(initial); return; }
    let isMounted = true;

    setPanelData((prev) => ({ ...prev, loading: true, error: null }));

    Promise.allSettled([getHomepageOffers(), fetchHomepageListings()]).then(([offersResult, listingsResult]) => {
      if (!isMounted) return;

      const offers = offersResult.status === 'fulfilled' && Array.isArray(offersResult.value)
        ? offersResult.value
        : [];
      const listings = listingsResult.status === 'fulfilled' && Array.isArray(listingsResult.value?.newListings)
        ? listingsResult.value.newListings
        : [];
      const bothFailed = offersResult.status === 'rejected' && listingsResult.status === 'rejected';

      setPanelData({
        offers,
        listings,
        loading: false,
        error: bothFailed ? 'Unable to load local highlights right now.' : null,
      });
    });

    return () => {
      isMounted = false;
    };
  }, [initial]);

  const hotDealRows = useMemo(() => {
    if (panelData.loading) return [];
    const rows = buildDealPanelRows(panelData.offers, { now: panelNow });
    return rows.length
      ? rows
      : [buildEmptyPanelRow({
        id: 'empty-deals',
        title: 'No active deals yet',
        subtitle: 'Browse the latest local offers as they are published.',
        href: '/offers',
      })];
  }, [panelData.loading, panelData.offers, panelNow]);

  const newBusinessRows = useMemo(() => {
    if (panelData.loading) return [];
    const rows = buildNewBusinessPanelRows(panelData.listings, { now: panelNow });
    return rows.length
      ? rows
      : [buildEmptyPanelRow({
        id: 'empty-businesses',
        title: 'No new businesses yet',
        subtitle: 'Explore the full local business directory.',
        href: '/listings',
      })];
  }, [panelData.loading, panelData.listings, panelNow]);

  const handleOptInChange = (event) => {
    const { checked, name, type, value } = event.target;
    setOptInForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleOptInSubmit = async (event) => {
    event.preventDefault();
    setOptInError('');

    try {
      const hasPhone = Boolean(optInForm.phone.trim());
      if (hasPhone && !optInForm.smsMarketingConsent) {
        setOptInError('Check the text-message consent box to subscribe with a mobile number.');
        return;
      }
      const normalizedPhone = hasPhone ? normalizePhoneToE164(optInForm.phone) : '';
      if (hasPhone && !normalizedPhone) {
        setOptInError('Enter a valid mobile number, including country code for non-US numbers.');
        return;
      }
      setOptInSubmitting(true);
      const fingerprint = JSON.stringify(optInForm);
      if (optInRequest.current?.fingerprint !== fingerprint) optInRequest.current = { fingerprint, id: crypto.randomUUID() };
      const result = await resolveLightweightProfile({
        email: optInForm.email,
        phone: normalizedPhone,
        visitorId: getOrCreateVisitorId(),
        source: 'homepage_opt_in',
        smsMarketingConsent: optInForm.smsMarketingConsent,
        smsConsentRequestId: optInRequest.current.id,
        smsConsentVersion: PUBLIC_SMS_CONSENT_VERSION,
        webOrigin: window.location.origin,
      });

      optInRequest.current = null;
      if (result.smsConfirmationReason === 'SMS_SUPPRESSED') setOptInError('Your request was saved. A previous STOP remains in effect; reply START to this community’s SMS number to resubscribe.');
      else if (result.smsConfirmationStatus === 'SKIPPED') setOptInError('Your request was saved, but SMS updates are not active. Please contact this community for help.');
      else { toast.success(result.verificationRequired ? 'Check your email to confirm your subscription.' : 'Your subscription was saved.', { id: 'action:HomeCommunitySections:handleOptInSubmit' }); }
      setOptInForm({ email: '', phone: '', smsMarketingConsent: false });
    } catch (error) {
      toast.error(error?.message || 'Unable to subscribe right now.', { id: 'action:HomeCommunitySections:handleOptInSubmit' });
    } finally {
      setOptInSubmitting(false);
    }
  };

  const hexToRgba = (hex, alpha) => {
    if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
      return `rgba(15, 43, 91, ${alpha})`;
    }

    const red = parseInt(hex.slice(1, 3), 16);
    const green = parseInt(hex.slice(3, 5), 16);
    const blue = parseInt(hex.slice(5, 7), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  return (
    <section className="bg-white py-12 lg:py-16">
      <PublicContainer>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-center">
          <Card padding="none" className="border w-full overflow-hidden lg:w-[560px]" style={{ borderColor: surfaceBorder }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: surfaceBorder }}>
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-xl font-semibold" style={{ color: headingColor }}>
                  <span className="inline-flex items-center gap-2">
                    <Flame size={16} style={{ color: '#EF4444' }} />
                    Hot Deals This Week
                  </span>
                </h2>
                <Link
                  to="/offers"
                  className="text-xs font-semibold text-[var(--primary)] transition-colors hover:text-[var(--primary-hover)]"
                >
                  View All Deals
                </Link>
              </div>
            </div>

            <div className="divide-y" style={{ borderColor: surfaceBorder }}>
              {panelData.loading ? <PanelSkeletonRows /> : null}
              {!panelData.loading && panelData.error ? (
                <InlineFeedback variant="error" className="px-4 py-3 text-xs text-slate-500">{panelData.error}</InlineFeedback>
              ) : null}
              {!panelData.loading && hotDealRows.map((deal) => (
                <button
                  key={deal.id}
                  type="button"
                  onClick={() => navigate(deal.href || '/offers')}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                >
                  {deal.imageUrl ? (
                    <img
                      src={deal.imageUrl}
                      alt={deal.title}
                      className="h-14 w-14 shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-600">
                      {deal.isEmpty ? <Tag size={18} /> : deal.fallbackInitials}
                    </div>
                  )}

                  <div className="min-w-0 flex-1 space-y-1">
                    {deal.isEmpty ? null : (
                      <Badge variant="success" size="xs" className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]">
                        {deal.badge}
                      </Badge>
                    )}
                    <p className="font-display text-sm font-semibold text-slate-900 line-clamp-1">{deal.title}</p>
                    <p className="text-xs text-slate-600 line-clamp-1">{deal.subtitle}</p>
                  </div>

                  <div className="flex items-center gap-2 pl-2">
                    {deal.isEmpty ? (
                      <span className="text-xs font-semibold text-[var(--primary)] whitespace-nowrap">View deals</span>
                    ) : (
                      <span className="text-xs font-semibold text-red-500 whitespace-nowrap">{deal.expires}</span>
                    )}
                    <ChevronRight size={16} className="text-slate-400" />
                  </div>
                </button>
              ))}
            </div>

            <div className="px-4 py-3 border-t" style={{ borderColor: surfaceBorder }}>
              <Button
                variant="outline"
                className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary-soft)]"
                onClick={() => navigate('/offers')}
              >
                <Tag size={14} />
                Browse All Deals
              </Button>
            </div>
          </Card>

          <Card padding="none" className="border w-full overflow-hidden lg:w-[560px]" style={{ borderColor: surfaceBorder }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: surfaceBorder }}>
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-xl font-semibold" style={{ color: headingColor }}>
                  <span className="inline-flex items-center gap-2">
                    <Sparkles size={16} style={{ color: '#6366F1' }} />
                    New This Week
                  </span>
                </h2>
                <Link
                  to="/listings"
                  className="text-xs font-semibold text-[var(--primary)] transition-colors hover:text-[var(--primary-hover)]"
                >
                  View All Businesses
                </Link>
              </div>
            </div>

            <div className="divide-y" style={{ borderColor: surfaceBorder }}>
              {panelData.loading ? <PanelSkeletonRows /> : null}
              {!panelData.loading && panelData.error ? (
                <InlineFeedback variant="error" className="px-4 py-3 text-xs text-slate-500">{panelData.error}</InlineFeedback>
              ) : null}
              {!panelData.loading && newBusinessRows.map((item, index) => (
                item.isEmpty ? (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(item.href || '/listings')}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-sm font-semibold text-slate-700">
                      <Sparkles size={16} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-display text-sm font-semibold text-slate-900 line-clamp-1">{item.title}</p>
                      <p className="text-xs text-slate-600 line-clamp-1">{item.subtitle}</p>
                    </div>

                    <div className="flex items-center gap-2 pl-2">
                      <span className="text-xs font-semibold text-[var(--primary)] whitespace-nowrap">Browse directory</span>
                      <ChevronRight size={16} className="text-slate-400" />
                    </div>
                  </button>
                ) : (
                  <div key={item.id} className="px-2 py-1.5">
                    <TrackableListingCard
                      listingId={item.listing?.id || item.id}
                      context="New This Week"
                      section="New This Week"
                      index={index}
                      variant="row"
                      tier={getTierKey(item.listing)}
                      listing={item.cardListing}
                      targetPath={item.href}
                      onNavigate={() => navigate(item.href || '/listings')}
                    />
                  </div>
                )
              ))}
            </div>

            <div className="px-4 py-3 border-t" style={{ borderColor: surfaceBorder }}>
              <Button
                variant="outline"
                className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary-soft)]"
                onClick={() => navigate('/listings')}
              >
                <Sparkles size={14} />
                Explore New Businesses
              </Button>
            </div>
          </Card>
        </div>

        <section
          aria-hidden="true"
          className="hidden"
          style={{
            borderColor: 'var(--secondary-soft)',
            backgroundColor: 'var(--secondary-soft)',
            color: 'var(--primary)',
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-[0.9fr,1fr,1.35fr,0.75fr]">
            <div className="relative min-h-[136px] border-b border-white/20 md:min-h-full md:border-b-0 md:border-r md:border-white/15">
              {optInImageUrl ? (
                <div
                  className="absolute inset-0 bg-cover"
                  style={{
                    backgroundImage: `url(${optInImageUrl})`,
                    backgroundPosition: `${optInFocalX}% ${optInFocalY}%`,
                  }}
                />
              ) : (
                <div
                  className="absolute inset-0"
                  style={{
                    background: `radial-gradient(circle at 20% 20%, ${hexToRgba(heroGlowBlue, 0.35)} 0%, transparent 42%), radial-gradient(circle at 75% 60%, ${hexToRgba(heroGlowGreen, 0.25)} 0%, transparent 45%), linear-gradient(135deg, var(--primary) 0%, var(--primary) 100%)`,
                  }}
                />
              )}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${hexToRgba(heroOverlay, 0.7)}, ${hexToRgba(heroOverlay, 0.15)})` }} />
            </div>

            <div className="border-b border-white/20 p-4 sm:p-5 md:border-b-0 md:border-r md:border-white/15 lg:p-6 flex h-full flex-col justify-center">
              <p className="font-display text-xl font-semibold leading-tight" style={{ color: 'var(--primary)' }}>Stay Connected with {spotlightName || 'My Town Spotlight'}</p>
              <p className="mt-2 text-sm" style={{ color: 'var(--primary)' }}>
                Get local deals, events, and hidden gems delivered straight to your inbox.
              </p>
            </div>

            <div className="border-b border-white/20 p-4 sm:p-5 md:border-b-0 md:border-r md:border-white/15 lg:p-6">
              <form className="grid grid-cols-1 gap-2.5" onSubmit={handleOptInSubmit}>
                <label className="sr-only" htmlFor="spotlight-email">Email Address</label>
                <Input
                  id="spotlight-email"
                  name="email"
                  type="email"
                  required={!optInForm.phone.trim()}
                  placeholder="Email Address"
                  value={optInForm.email}
                  startIcon={<Mail size={14} style={{ color: subtleColor }} />}
                  className="h-10 bg-white text-sm"
                  style={{ '--tw-ring-color': optInInputRingColor, borderColor: inputBorder, color: inputText }}
                  onChange={handleOptInChange}
                  onFocus={(event) => {
                    event.currentTarget.style.borderColor = optInInputFocusColor;
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.borderColor = inputBorder;
                  }}
                />

                <Input
                  name="phone"
                  type="tel"
                  placeholder="Mobile Number (optional)"
                  value={optInForm.phone}
                  aria-invalid={Boolean(optInError && /mobile number/i.test(optInError))}
                  startIcon={<Phone size={14} style={{ color: subtleColor }} />}
                  className="h-10 bg-white text-sm"
                  style={{ '--tw-ring-color': optInInputRingColor, borderColor: inputBorder, color: inputText }}
                  onChange={handleOptInChange}
                  onFocus={(event) => {
                    event.currentTarget.style.borderColor = optInInputFocusColor;
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.borderColor = inputBorder;
                  }}
                />

                <label className="flex items-start gap-2 text-[11px] leading-4" style={{ color: 'var(--primary)' }}>
                  <input
                    name="smsMarketingConsent"
                    type="checkbox"
                    checked={optInForm.smsMarketingConsent}
                    required={Boolean(optInForm.phone.trim())}
                    onChange={handleOptInChange}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
                  />
                  <span>
                    {PUBLIC_SMS_CONSENT_TEXT}
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={optInSubmitting}
                  className="h-10 w-full px-4 text-sm bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--primary-hover)]"
                >
                  {optInSubmitting ? 'Saving...' : 'Sign Me Up!'}
                </Button>

                {optInError ? (
                  <InlineFeedback variant="error" className="text-[11px] text-red-600" role="alert">{optInError}</InlineFeedback>
                ) : null}
              </form>
            </div>

            <div className="flex h-full items-center justify-center gap-3 p-4 text-center lg:p-6">
              <Gift size={24} style={{ color: 'var(--primary)', opacity: 0.9 }} />
              <p className="text-sm font-semibold leading-snug" style={{ color: 'var(--primary)' }}>
                Join &amp; get a FREE Local
                <br />
                Deals Guide!
              </p>
            </div>
          </div>
        </section>
        <PublicOptInPanel source="homepage_opt_in" className="mt-8" />
      </PublicContainer>
    </section>
  );
}

export default HomeCommunitySections;
